import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { ensureBlockExpiry, purgeExpiredBlocks } from '../game/engine';
import { makeBlockTile } from '../game/tiles';

export const useRoomConnection = ({ applyServerState, setLastEvent, setSoloMode, onEnterGame }) => {
  const [roomCode, setRoomCode] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('offline');
  const [playerId, setPlayerId] = useState('');
  const [opponent, setOpponent] = useState(null);
  const [roomStarted, setRoomStarted] = useState(false);
  const [canStart, setCanStart] = useState(false);
  const [hostId, setHostId] = useState('');
  const [winnerId, setWinnerId] = useState('');
  const socketRef = useRef(null);
  const applyServerStateRef = useRef(applyServerState);
  const targetedBlocksRef = useRef([]);
  const lastRoundIdRef = useRef(0);

  useEffect(() => {
    applyServerStateRef.current = applyServerState;
  }, [applyServerState]);

  const connectSocket = useCallback(
    (onConnected) => {
      const socket = socketRef.current;
      if (!socket) return false;
      if (socket.connected) return true;
      socket.connect();
      if (onConnected) {
        const handler = () => {
          socket.off('connect', handler);
          onConnected();
        };
        socket.on('connect', handler);
      }
      return false;
    },
    []
  );

  const mergeTargetedBlocks = useCallback((tiles, now) => {
    const aliveBlocks = [];
    const merged = [...tiles];
    targetedBlocksRef.current = targetedBlocksRef.current.filter((b) => b.expiresAt > now);
    targetedBlocksRef.current.forEach((block) => {
      const occupied = merged.some((t) => t.row === block.row && t.col === block.col);
      if (!occupied) {
        merged.push(block);
      }
      aliveBlocks.push(block);
    });
    targetedBlocksRef.current = aliveBlocks;
    return merged;
  }, []);

  useEffect(() => {
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL ||
      (process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : undefined);

    const socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnectionStatus('connected');
      if (setLastEvent) setLastEvent((prev) => (prev === 'Verbinde zum Server...' ? 'Verbunden' : prev));
    });
    socket.on('disconnect', () => {
      setConnectionStatus('offline');
      if (setLastEvent) setLastEvent((prev) => (prev === 'Verbinde zum Server...' ? 'Keine Serververbindung' : prev));
    });
    socket.on('connect_error', () => {
      setConnectionStatus('offline');
      if (setLastEvent) setLastEvent('Keine Serververbindung');
    });
    socket.on('roomCreated', ({ roomId, playerId: pid }) => {
      setRoomCode(roomId);
      setPlayerId(pid);
      setSoloMode(false);
      setRoomStarted(false);
      if (onEnterGame) onEnterGame();
      if (setLastEvent) setLastEvent(`Neuer Raum ${roomId} erstellt`);
    });
    socket.on('roomJoined', ({ roomId, playerId: pid }) => {
      setRoomCode(roomId);
      setPlayerId(pid);
      setSoloMode(false);
      setRoomStarted(false);
      if (onEnterGame) onEnterGame();
      if (setLastEvent) setLastEvent(`Room ${roomId} beigetreten`);
    });
    socket.on(
      'state',
      ({
        you,
        opponent: opp,
        started,
        canStart: canStartPayload,
        hostId: hostIdPayload,
        winnerId: winnerIdPayload,
        roundId: roundIdPayload,
      }) => {
        const now = Date.now();
        const roundId = roundIdPayload || 0;
        if (roundId !== lastRoundIdRef.current) {
          targetedBlocksRef.current = [];
          lastRoundIdRef.current = roundId;
        }
        if (you && applyServerStateRef.current) {
          applyServerStateRef.current({ ...you, roundId }, now);
        }
        setRoomStarted(!!started);
        setCanStart(!!canStartPayload);
        setHostId(hostIdPayload || '');
        setWinnerId(winnerIdPayload || '');
        setOpponent((prevOpp) => {
          if (!opp) return null;
          const incomingTiles = Array.isArray(opp.tiles)
            ? opp.tiles
            : Array.isArray(opp.state?.tiles)
              ? opp.state.tiles
              : prevOpp?.tiles || [];
          const frozenUntilIncoming = opp.frozenUntil ?? opp.state?.frozenUntil ?? prevOpp?.frozenUntil ?? 0;
          const colorShiftMapIncoming =
            (opp.colorShiftMap ?? opp.state?.colorShiftMap ?? prevOpp?.colorShiftMap) || null;
          const colorShiftUntilIncoming =
            opp.colorShiftUntil ?? opp.state?.colorShiftUntil ?? prevOpp?.colorShiftUntil ?? 0;
          const withTargeted = mergeTargetedBlocks(incomingTiles, now);
          return {
            ...opp,
            frozenUntil: frozenUntilIncoming,
            colorShiftMap: colorShiftMapIncoming,
            colorShiftUntil: colorShiftUntilIncoming,
            tiles: withTargeted.map((t) => ensureBlockExpiry(t, now)),
          };
        });
      }
    );
    socket.on('errorMsg', (msg) => setLastEvent(msg));

    return () => {
      socket.disconnect();
    };
  }, [mergeTargetedBlocks, onEnterGame, setLastEvent, setSoloMode]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setOpponent((prev) => {
        if (!prev) return prev;
        const cleaned = purgeExpiredBlocks(prev.tiles || [], now);
        const merged = mergeTargetedBlocks(cleaned, now);
        const colorActive = prev.colorShiftUntil && prev.colorShiftUntil > now;
        const tilesChanged = merged.length !== (prev.tiles || []).length;
        const colorCleared = !colorActive && prev.colorShiftMap;
        if (!tilesChanged && !colorCleared) return prev;
        return {
          ...prev,
          tiles: merged.map((t) => ensureBlockExpiry(t, now)),
          ...(colorActive ? {} : { colorShiftMap: null, colorShiftUntil: 0 }),
        };
      });
    }, 500);
    return () => clearInterval(timer);
  }, [mergeTargetedBlocks]);

  const isOnline = !!(roomCode && socketRef.current && socketRef.current.connected);
  const isHost = (playerId && playerId === hostId) || connectionStatus === 'host';
  const isWinner = winnerId && playerId && winnerId === playerId;
  const isLoser = winnerId && playerId && winnerId !== playerId;
  const opponentFreezeSeconds = opponent?.frozenUntil
    ? Math.max(0, Math.ceil((opponent.frozenUntil - Date.now()) / 1000))
    : 0;

  const hostRoom = useCallback(
    (playerName) => {
      const socket = socketRef.current;
      if (!socket) {
        if (setLastEvent) setLastEvent('Keine Serververbindung');
        return;
      }
      const emitCreate = () => {
        socket.emit('createRoom', { name: playerName || 'Host' });
        if (onEnterGame) onEnterGame();
      };
      if (socket.connected) {
        emitCreate();
        setConnectionStatus('host');
        setSoloMode(false);
      } else {
        if (setLastEvent) setLastEvent('Verbinde zum Server...');
        connectSocket(() => {
          emitCreate();
          setConnectionStatus('host');
          setSoloMode(false);
        });
      }
    },
    [connectSocket, onEnterGame, setLastEvent, setSoloMode]
  );

  const joinRoom = useCallback(
    (joinCode, playerName) => {
      const sanitized = joinCode.replace(/\D/g, '');
      if (!/^\d{5}$/.test(sanitized)) {
        if (setLastEvent) setLastEvent('Bitte 5-stelligen Room-Code eingeben');
        return;
      }
      const socket = socketRef.current;
      if (!socket) {
        if (setLastEvent) setLastEvent('Keine Serververbindung');
        return;
      }
      const code = sanitized;
      const emitJoin = () => {
        socket.emit('joinRoom', { roomId: code, name: playerName || 'Spieler' });
        if (onEnterGame) onEnterGame();
      };
      if (socket.connected) {
        emitJoin();
        setConnectionStatus('joined');
        setSoloMode(false);
      } else {
        if (setLastEvent) setLastEvent('Verbinde zum Server...');
        connectSocket(() => {
          emitJoin();
          setConnectionStatus('joined');
          setSoloMode(false);
        });
      }
    },
    [connectSocket, onEnterGame, setLastEvent, setSoloMode]
  );

  const startGame = useCallback(() => {
    if (!roomCode) return;
    socketRef.current?.emit('startGame', { roomId: roomCode });
  }, [roomCode]);

  const emitMove = useCallback(
    (direction) => {
      if (!roomCode) return;
      socketRef.current?.emit('move', { roomId: roomCode, direction });
    },
    [roomCode]
  );

  const emitPowerup = useCallback(
    (type, payload = {}) => {
      if (!roomCode) return;
      socketRef.current?.emit('usePowerup', { roomId: roomCode, type, ...payload });
    },
    [roomCode]
  );

  const emitTargetedGarbage = useCallback(
    (row, col) => {
      if (!roomCode) return;
      socketRef.current?.emit('usePowerup', { roomId: roomCode, type: 'targetedGarbage', row, col });
    },
    [roomCode]
  );

  const addTargetedGarbageToOpponent = useCallback((row, col) => {
    let applied = false;
    const now = Date.now();
    const newBlock = makeBlockTile(row, col, () => now);
    targetedBlocksRef.current = targetedBlocksRef.current
      .filter((b) => b.expiresAt > now)
      .concat({ ...newBlock, expiresAt: now + 20000 });
    setOpponent((prev) => {
      if (!prev) return prev;
      const occupied = prev.tiles?.some((t) => t.row === row && t.col === col);
      if (occupied) return prev;
      applied = true;
      return { ...prev, tiles: [...(prev.tiles || []), newBlock] };
    });
    return applied;
  }, []);

  const restart = useCallback(() => {
    if (!roomCode) return;
    socketRef.current?.emit('restart', { roomId: roomCode });
  }, [roomCode]);

  const goOffline = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.connected && roomCode) {
      socket.emit('leaveRoom', { roomId: roomCode, playerId });
    }
    socket?.disconnect();
    setRoomCode('');
    setConnectionStatus('offline');
    setOpponent(null);
    setPlayerId('');
    setWinnerId('');
    setCanStart(false);
    setHostId('');
    setRoomStarted(false);
    targetedBlocksRef.current = [];
    lastRoundIdRef.current = 0;
  }, [playerId, roomCode]);

  const clearWinner = useCallback(() => {
    setWinnerId('');
  }, []);

  return {
    roomCode,
    connectionStatus,
    playerId,
    opponent,
    roomStarted,
    canStart,
    hostId,
    winnerId,
    isOnline,
    isHost,
    isWinner,
    isLoser,
    opponentFreezeSeconds,
    hostRoom,
    joinRoom,
    startGame,
    emitMove,
    emitPowerup,
    emitTargetedGarbage,
    restart,
    goOffline,
    clearWinner,
    addTargetedGarbageToOpponent,
  };
};
